import {
  Button,
  Flex,
  Group,
  Pagination,
  Stack,
  Text,
  Title
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconUserPlus } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useFeatureUsage } from '@/components/subscriptions/useFeatureUsage'
import { useUsageLimitModal } from '@/components/subscriptions/useUsageLimitModal'
import { VP_NO_REPLY_EMAIL } from '@/config/main'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetWorkspaceMembers } from '@/queries/workspaceQueries'
import { useSession } from '@/session/SessionContext'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { buildSortParams } from '@/utils/sorting'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { InviteMembersModal } from './InviteMembers/InviteMembersModal'
import { MembersList, TableSortConfig } from './MembersList/MembersList'
import { useWorkspace } from './WorkspaceContext'
import { SpaceRemainingCount } from './WorkspaceMembers/SpaceRemainingCount'

const PAGE_SIZE = 20

const DEFAULT_SORT_CONFIG: TableSortConfig = {
  key: 'full_name',
  order: 'asc'
}

export const WorkspaceMembersPage = () => {
  const { user } = useSession()
  const { workspace } = useWorkspace()

  const [
    isInviteMemberModalOpened,
    { open: openInviteMemberModal, close: closeInviteMemberModal }
  ] = useDisclosure(false)

  const [canInviteMembers] = useHasPermissions(Resource.WorkspaceUsers, [
    CrudAction.Create
  ])

  const [tableState, setTableState] = useTableState({
    activePage: 1,
    sortConfig: DEFAULT_SORT_CONFIG
  })

  const { activePage, sortConfig } = tableState

  const { data, isFetching, isLoading, isError, refetch } =
    useGetWorkspaceMembers(
      workspace.id,
      {
        offset: getPageOffset(activePage, PAGE_SIZE),
        limit: PAGE_SIZE,
        ordering: buildSortParams(sortConfig).ordering
      },
      {
        keepPreviousData: true
      }
    )

  const members = data?.results || []
  const totalPageCount = getPageCount(data?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const { usage } = useFeatureUsage('number_of_users')
  const { openUsageLimitModal } = useUsageLimitModal()

  const hasRemainingUsers = !usage || usage.remaining > 0

  const handleInviteMemberClick = () => {
    if (hasRemainingUsers) {
      openInviteMemberModal()
    } else {
      openUsageLimitModal('number_of_users', {
        maxUsers: usage?.limit
      })
    }
  }

  return (
    <>
      <PlatformAlert mb="xl">
        <Stack>
          <Text size="sm">
            <FormattedMessage
              id="workspace.members.invite.info1"
              values={{ email: <strong>{VP_NO_REPLY_EMAIL}</strong> }}
            />
          </Text>

          <Text size="sm">
            <FormattedMessage id="workspace.members.invite.info2" />
          </Text>
        </Stack>
      </PlatformAlert>

      <Group justify="space-between" gap="sm" mb="sm">
        <Title order={3}>
          <FormattedMessage id="workspace.members" />
        </Title>

        {canInviteMembers && (
          <Group gap="xl" align="center">
            {usage && !usage.isUnlimited && (
              <SpaceRemainingCount count={usage.remaining} />
            )}

            <Button
              leftSection={<IconUserPlus size={16} />}
              size="sm"
              onClick={handleInviteMemberClick}
            >
              <FormattedMessage id="workspace.members.invite" />
            </Button>
          </Group>
        )}
      </Group>

      <MembersList
        workspace={workspace}
        members={members}
        sortConfig={sortConfig}
        myUser={user || undefined}
        isLoading={isLoading || isFetching}
        hasError={isError}
        onSort={(sortConfig) => setTableState({ activePage: 1, sortConfig })}
        onReload={() => void refetch()}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <InviteMembersModal
        workspaceId={workspace.id}
        isOpen={isInviteMemberModalOpened}
        onClose={closeInviteMemberModal}
      />
    </>
  )
}
